"use client";

// BigBlocks mnemonic-flow/mnemonic-grid-ui, b-open-io/bigblocks@38553c5.
// Local adaptations: ordered-list semantics and wrapping visible words.

import { useCallback } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single word slot that can be read-only, editable, or a verification blank */
export type WordSlotMode = "readonly" | "editable" | "blank";

/** Props for an individual word slot in the grid */
export interface WordSlotProps {
	/** 1-based position number */
	position: number;
	/** The word to display (empty string for blanks) */
	word: string;
	/** How the slot behaves */
	slotMode: WordSlotMode;
	/** Called when the user changes a word (editable or blank mode) */
	onChange?: (value: string) => void;
	/** Placeholder text for input fields */
	placeholder?: string;
}

/** Props for the full mnemonic grid */
export interface MnemonicGridUiProps {
	/** The word list to render */
	words: string[];
	/** How many columns to show (default: 4) */
	columns?: 3 | 4;
	/** Set of positions (0-indexed) that should be editable inputs */
	editablePositions?: Set<number>;
	/** Set of positions (0-indexed) that are verification blanks */
	blankPositions?: Set<number>;
	/** Called when a word changes (index, value) */
	onWordChange?: (index: number, value: string) => void;
	/** Values for blank/verification positions keyed by index */
	blankValues?: Record<number, string>;
	/** Called when a blank value changes (index, value) */
	onBlankChange?: (index: number, value: string) => void;
	/** Render only verification blanks; omit all expected words from the DOM. */
	onlyBlankPositions?: boolean;
	/** Additional CSS classes */
	className?: string;
}

// ---------------------------------------------------------------------------
// Word Slot
// ---------------------------------------------------------------------------

function WordSlot({
	position,
	word,
	slotMode,
	onChange,
	placeholder,
}: WordSlotProps) {
	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e.target.value);
		},
		[onChange]
	);

	return (
		<li
			className={cn(
				"flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2",
				slotMode === "blank" && "border-primary/50 bg-primary/5"
			)}
		>
			<span className="w-6 shrink-0 text-right font-mono text-muted-foreground text-xs">
				{position}.
			</span>
			{slotMode === "readonly" ? (
				<span className="select-all break-all font-mono text-foreground text-sm">
					{word}
				</span>
			) : (
				<Input
					aria-label={`Word ${position}`}
					autoCapitalize="none"
					autoComplete="off"
					autoCorrect="off"
					className="h-auto border-0 bg-transparent p-0 font-mono text-sm shadow-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					onChange={handleChange}
					placeholder={placeholder ?? `word ${position}`}
					spellCheck={false}
					type="text"
					value={word}
				/>
			)}
		</li>
	);
}

// ---------------------------------------------------------------------------
// Mnemonic Grid
// ---------------------------------------------------------------------------

/**
 * A numbered grid of mnemonic seed words.
 *
 * Supports three slot modes:
 * - **readonly**: Displays the word as text (display/create modes)
 * - **editable**: Renders an input for the user to type a word (import mode)
 * - **blank**: Shows an empty input the user must fill in (verify mode)
 *
 * Pure presentation component — receives all data and callbacks via props.
 */
export function MnemonicGridUi({
	words,
	columns = 4,
	editablePositions,
	blankPositions,
	onWordChange,
	blankValues,
	onBlankChange,
	onlyBlankPositions = false,
	className,
}: MnemonicGridUiProps) {
	const gridCols =
		columns === 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-4";

	return (
		<ol
			aria-label="Recovery phrase in order"
			className={cn("grid gap-2", gridCols, className)}
		>
			{words.map((word, index) => {
				const isBlank = blankPositions?.has(index) ?? false;
				if (onlyBlankPositions && !isBlank) {
					return null;
				}
				let slotMode: WordSlotMode = "readonly";
				let callback = onWordChange;
				let displayWord = word;
				if (editablePositions?.has(index)) {
					slotMode = "editable";
				}
				if (isBlank) {
					slotMode = "blank";
					callback = onBlankChange;
					displayWord = blankValues?.[index] ?? "";
				}
				return (
					<WordSlot
						// biome-ignore lint/suspicious/noArrayIndexKey: Phrase position is the immutable slot identity.
						key={index}
						onChange={(value) => callback?.(index, value)}
						placeholder={`word ${index + 1}`}
						position={index + 1}
						slotMode={slotMode}
						word={displayWord}
					/>
				);
			})}
		</ol>
	);
}
